import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { flexRender, Table as ReactTable } from "@tanstack/react-table";
import classNames from "classnames";
import { Meta } from "../hooks/useSignatureListColumns.ts";

interface CardsTableProps<T extends { id: string }> {
  table: ReactTable<T>;
  isRowSelected?: (id: string) => boolean;
  isErrorRow?: (row: T) => boolean;
  isRowDeemphasized?: (row: T, cellIndex: number) => boolean;
}

/*
 * A percentage width keeps the index column at content width: auto-layout
 * tables distribute excess width over the px-sized columns, not over
 * percentage-sized ones.
 */
const INDEX_COLUMN_WIDTH = "1%";

export const CardsTable = <T extends { id: string }>({
  table,
  isRowSelected = () => false,
  isErrorRow,
  isRowDeemphasized,
}: CardsTableProps<T>) => {
  const theme = useTheme();
  const dimmedRowStyle = {
    color: theme.palette.text.secondary,
    opacity: 0.7,
  };

  return (
    <TableContainer component={Paper} sx={{ overflow: "scroll" }}>
      <Table stickyHeader size="small">
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              <TableCell sx={{ width: INDEX_COLUMN_WIDTH }} align="right">
                #
              </TableCell>
              {headerGroup.headers.map((header) => (
                <TableCell
                  style={{ width: `${header.getSize()}px` }}
                  align={(header.column.columnDef.meta as Meta)?.align}
                  key={header.id}
                >
                  {header.isPlaceholder ? null : (
                    <TableSortLabel
                      className={classNames({
                        "cursor-pointer select-none":
                          header.column.getCanSort(),
                      })}
                      onClick={header.column.getToggleSortingHandler()}
                      active={header.column.getIsSorted() !== false}
                      direction={header.column.getIsSorted() || undefined}
                      disabled={!header.column.getCanSort()}
                      sx={{
                        // Without the focus ripple of other buttons, which
                        // TableSortLabel disables, keyboard focus would be
                        // invisible; show the browser's focus ring instead.
                        "&.Mui-focusVisible": { outline: "revert" },
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row, index) => {
            const isError = isErrorRow?.(row.original) ?? false;
            const rowContent = (
              <TableRow
                key={row.id}
                selected={isRowSelected(row.original.id)}
                sx={{
                  ...(isError
                    ? {
                        backgroundColor: alpha(theme.palette.error.main, 0.15),
                        "& > td": { backgroundColor: "transparent" },
                      }
                    : {}),
                }}
              >
                <TableCell sx={{ width: INDEX_COLUMN_WIDTH }} align="right">
                  {index + 1}
                </TableCell>
                {row.getVisibleCells().map((cell, cellIndex) => (
                  <TableCell
                    key={cell.id}
                    align={(cell.column.columnDef.meta as Meta)?.align}
                    sx={{
                      paddingTop: theme.spacing(1),
                      paddingBottom: theme.spacing(1),
                      ...(isRowDeemphasized?.(row.original, cellIndex)
                        ? dimmedRowStyle
                        : {}),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            );

            return rowContent;
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
